/**
 * Streaming text renderer with per-chunk fade-in. Each delta is appended
 * as a `<span class="fade">` inside the active `<p>` so CSS animates the
 * entrance. When the delta contains `\n\n` we close the current paragraph
 * and start a new one — without this, multi-paragraph replies render as
 * a single visual block during streaming and then visibly reflow when
 * `setFinalMarkdown` swaps in the markdown-rendered version.
 *
 * The root div also carries `markdown-content` so the existing
 * paragraph / list / heading styles apply to both the live-streamed
 * `<p>`s and the final markdown-rendered HTML.
 */

import { renderMarkdown } from './markdown';

export interface StreamingTextController {
  el: HTMLElement;
  appendDelta(text: string): void;
  setFinalMarkdown(text: string): void;
  reset(): void;
}

export function createStreamingText(): StreamingTextController {
  const el = document.createElement('div');
  // `markdown-content` so `.markdown-content p { margin:.7em 0 }` (and
  // friends) style the streamed paragraphs the same way they style the
  // post-stream markdown render. Ensures no visual jump when
  // setFinalMarkdown swaps the children.
  el.className = 'sp-streaming-text markdown-content';

  // Active <p> we're appending fade spans into. Reset to null whenever
  // the model emits a \n\n so the next chunk starts a fresh paragraph.
  let currentPara: HTMLElement | null = null;
  const ensurePara = (): HTMLElement => {
    if (!currentPara) {
      currentPara = document.createElement('p');
      el.appendChild(currentPara);
    }
    return currentPara;
  };

  // Bold is banned in agent chat and the final render strips its markers
  // (core/markdown.ts). While streaming, deltas were painted raw, so a
  // `**` the model emitted anyway sat on screen as asterisks until the
  // swap — and stayed there whenever a turn never reached the swap (a
  // navigation, an error). Strip the markers as they arrive. A lone
  // trailing `*` is held back one delta so a `**` split across chunks
  // is still caught; reset() and setFinalMarkdown() clear it.
  let carry = '';
  const stripBoldMarkers = (raw: string): string => {
    let text = carry + raw;
    carry = '';
    if (text.endsWith('*') && !text.endsWith('**')) {
      carry = '*';
      text = text.slice(0, -1);
    }
    return text.replace(/\*\*/g, '').replace(/__/g, '');
  };

  return {
    el,
    appendDelta(raw) {
      if (!raw) return;
      const text = stripBoldMarkers(raw);
      if (!text) return;
      // Split on one-or-more blank-line breaks. Empty leading/trailing
      // parts (when the delta starts/ends with \n\n) are skipped — we
      // just close the current paragraph and let the next non-empty
      // part open the next one.
      const parts = text.split(/\n{2,}/);
      parts.forEach((part, i) => {
        if (i > 0) {
          // Paragraph boundary — start a fresh <p> on the next non-
          // empty chunk.
          currentPara = null;
        }
        if (!part) return;
        const span = document.createElement('span');
        span.className = 'fade';
        span.textContent = part;
        ensurePara().appendChild(span);
      });
    },
    setFinalMarkdown(text) {
      carry = '';
      el.classList.add('sp-streaming-text--final');
      el.innerHTML = renderMarkdown(text);
      currentPara = null;
    },
    reset() {
      carry = '';
      el.innerHTML = '';
      el.classList.remove('sp-streaming-text--final');
      currentPara = null;
    },
  };
}
